import { maskedRegionProps } from "@koaform/analytics";
import { Logo } from "@koaform/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Share } from "../share/Share.js";
import { ResponseDetail } from "./ResponseDetail.js";

import { usePageTitle } from "../hooks/usePageTitle.js";
import { api, type CsvColumn, type ResponseRow } from "../lib/api.js";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const RENDERER_URL =
  import.meta.env.VITE_RENDERER_URL ?? "http://localhost:5052";

/**
 * The responses table.
 *
 * Everything below the header is wrapped in `maskedRegionProps`. Clarity
 * masking is relaxed across this app, which is right for the operator's own
 * UI — but every cell here was typed by a stranger, and can be a home address,
 * a salary or a medical detail. Those must never reach a session replay.
 */
export const Responses = ({ formId }: { formId: string }) => {
  const [columns, setColumns] = useState<CsvColumn[]>([]);
  const [rows, setRows] = useState<ResponseRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    title: string;
    slug: string | null;
    responses: number;
  } | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  usePageTitle(summary ? `Responses · ${summary.title}` : "Responses");

  useEffect(() => {
    void (async () => {
      try {
        const [head, page] = await Promise.all([
          api.formSummary(formId),
          api.listResponses(formId),
        ]);
        setSummary(head);
        setColumns(page.columns);
        setRows(page.responses);
        setCursor(page.nextCursor);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not load.");
      } finally {
        setLoading(false);
      }
    })();
  }, [formId]);

  const loadMore = async () => {
    if (!cursor) return;
    const page = await api.listResponses(formId, cursor);
    setRows((prev) => [...prev, ...page.responses]);
    setCursor(page.nextCursor);
  };

  const remove = async (id: string) => {
    await api.deleteResponse(formId, id);
    setRows((prev) => prev.filter((r) => r.id !== id));
    setSummary((s) => (s ? { ...s, responses: Math.max(0, s.responses - 1) } : s)); // prettier-ignore
  };

  if (loading) return <p className="p-8 text-sm text-faint">Loading…</p>;
  if (error)
    return (
      <p role="alert" className="p-8 text-sm text-danger">
        {error}
      </p>
    );

  return (
    <div className="min-h-dvh bg-paper font-sans antialiased">
      <header className="border-b border-line-strong bg-surface">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div className="flex min-w-0 items-center gap-4">
            <a href="/" aria-label="koaform" className="flex-none">
              <Logo size={26} />
            </a>
            <a
              href="/"
              className="hidden flex-none text-[13px] font-semibold text-muted hover:text-ink sm:block"
            >
              ← My forms
            </a>
            <span className="hidden h-6 w-px flex-none bg-line-strong sm:block" />
            <div className="min-w-0">
              <h1 className="truncate text-lg font-extrabold tracking-[-0.02em] text-ink">
                {summary?.title}
              </h1>
              <p className="font-mono text-[11px] text-faint">
                {summary?.responses ?? 0}{" "}
                {summary?.responses === 1 ? "response" : "responses"}
              </p>
            </div>
          </div>
          <div className="flex flex-none items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/forms/${formId}/edit`)}
              className="rounded-[9px] border border-line-bold bg-surface px-3.5 py-2 text-sm font-semibold text-ink hover:border-primary-600"
            >
              Edit form
            </button>
            {summary?.slug ? (
              <a
                href={`${RENDERER_URL}/r/${summary.slug}`}
                className="rounded-[9px] border border-line-bold bg-surface px-3.5 py-2 text-sm font-semibold text-ink hover:border-primary-600 hover:text-ink"
              >
                Open form
              </a>
            ) : null}
            {/* A plain link, not fetch: the browser handles the download stream. */}
            <a
              href={`${API_URL}/forms/${formId}/responses/export.csv`}
              className="rounded-[9px] bg-primary-600 px-3.5 py-2 text-sm font-bold text-white hover:bg-primary-700 hover:text-white"
            >
              Export CSV
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        {rows.length === 0 ? (
          <div className="rounded-[20px] border-[1.5px] border-dashed border-line-bold bg-surface px-8 py-14 text-center">
            <p className="m-0 mb-2 text-[19px] font-bold tracking-[-0.02em]">
              No responses yet
            </p>
            <p className="mx-auto m-0 max-w-[380px] text-[15px] leading-[1.55] text-muted">
              Share your form and answers will appear here as they arrive.
            </p>
          </div>
        ) : (
          <div
            {...maskedRegionProps}
            className="overflow-x-auto rounded-lg border border-line-strong bg-surface"
          >
            <table className="min-w-full divide-y divide-line-strong text-sm">
              <thead className="bg-subtle">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-muted">
                    Submitted
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col.fieldId}
                      className="px-4 py-2 text-left font-semibold whitespace-nowrap text-muted"
                    >
                      {col.label}
                      {col.removed ? (
                        <span
                          title="This question was deleted, but these answers are kept."
                          className="ml-1 font-normal text-faint"
                        >
                          (removed)
                        </span>
                      ) : null}
                    </th>
                  ))}
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setOpenId(row.id)}
                    className="cursor-pointer hover:bg-paper"
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-muted">
                      {new Date(row.submittedAt).toLocaleString()}
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col.fieldId}
                        className="max-w-xs truncate px-4 py-2 text-ink"
                        title={row.cells[col.fieldId]}
                      >
                        {row.cells[col.fieldId]}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void remove(row.id);
                        }}
                        className="text-xs text-faint hover:text-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cursor ? (
          <button
            type="button"
            onClick={() => void loadMore()}
            className="mt-4 rounded-[10px] border border-line-bold bg-surface px-3 py-2 text-sm font-semibold text-body hover:bg-paper"
          >
            Load more
          </button>
        ) : null}

        {summary?.slug ? (
          <div className="mt-8">
            <Share slug={summary.slug} />
          </div>
        ) : null}
      </main>

      {openId ? (
        <ResponseDetail
          formId={formId}
          responseId={openId}
          onClose={() => setOpenId(null)}
          onDelete={() => {
            void remove(openId);
            setOpenId(null);
          }}
        />
      ) : null}
    </div>
  );
};
